/**
 * Os campos da leitura do comprovante de crédito que o painel corrige (E203.F4, ADR-0146 §7): o
 * que a leitura achou em cada campo da linha clicada, a fonte (o documento de onde o valor saiu)
 * e a página. Controlado por quem o monta; a gravação fica em `correcaoDoCredito`.
 *
 * E207.F7.C01 (owner, 01/11): separado do formulário para servir ao painel da linha do crédito
 * pelo resultado, que tem um fundamento e um Salvar só.
 */
import { Field, Input, Select } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { nomeDoDocumento } from '@/lib/exame_eletronico_peca';
import type { CampoDoCreditoLido, CreditoNoPainelData } from '@/lib/revisaoDoDado';

import type { FonteDoValor, Valores } from './correcaoDoCredito';
import type { Mesa } from './tipos';

const t = ptBR.painelDoDado.credito;

export function CamposDoCredito({
  alvo,
  campos,
  valores,
  onValores,
  fonte,
  onFonte,
  mesa,
}: {
  alvo: CreditoNoPainelData;
  campos: readonly CampoDoCreditoLido[];
  valores: Valores;
  onValores: (valores: Valores) => void;
  fonte: FonteDoValor;
  onFonte: (fonte: FonteDoValor) => void;
  mesa: Mesa;
}): JSX.Element {
  const documentos = [
    { id: alvo.documento_id, nome: t.pagina.replace('{pagina}', String(alvo.pagina)) },
    ...(mesa.ccbs ?? [])
      .filter((p) => p.source_document_id !== alvo.documento_id)
      .map((p) => ({ id: p.source_document_id, nome: nomeDoDocumento(p) ?? p.original_filename })),
  ];
  return (
    <>
      {campos.map((campo) => (
        <Field key={campo} label={t.campos[campo]} htmlFor={`painel-credito-${campo}`}>
          <Input
            id={`painel-credito-${campo}`}
            type={campo === 'data' ? 'date' : 'text'}
            value={valores[campo] ?? ''}
            onChange={(e) => onValores({ ...valores, [campo]: e.target.value })}
            data-testid={`painel-credito-${campo}`}
          />
        </Field>
      ))}
      <Field label={t.fonte} htmlFor="painel-credito-fonte">
        <Select
          id="painel-credito-fonte"
          value={fonte.documentoId}
          onChange={(e) => onFonte({ ...fonte, documentoId: e.target.value })}
          data-testid="painel-credito-fonte"
        >
          {documentos.map((d) => (
            <option key={d.id} value={d.id}>
              {d.nome}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t.paginaDaFonte} htmlFor="painel-credito-pagina">
        <Input
          id="painel-credito-pagina"
          type="number"
          min={1}
          value={fonte.pagina}
          onChange={(e) => onFonte({ ...fonte, pagina: e.target.value })}
          data-testid="painel-credito-pagina"
        />
      </Field>
    </>
  );
}
